import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Trophy, Clock, Target, TrendingUp } from 'lucide-react';
import { AnalyticsData } from '@/types/career';

interface AnalyticsProps {
  data: AnalyticsData;
}

export function Analytics({ data }: AnalyticsProps) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Your Progress</h2>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Overall Progress"
          value={`${data.overallProgress}%`}
          icon={<Target className="w-5 h-5" />}
          color="blue"
        >
          <Progress value={data.overallProgress} className="mt-2" />
        </StatCard>

        <StatCard
          title="Achievements"
          value={`${data.completedAchievements}/${data.totalAchievements}`}
          icon={<Trophy className="w-5 h-5" />}
          color="yellow"
        />

        <StatCard
          title="Time Invested"
          value={`${data.timeSpent} hours`}
          icon={<Clock className="w-5 h-5" />}
          color="green"
        />

        <StatCard
          title="Growth Trend"
          value="+23% this month"
          icon={<TrendingUp className="w-5 h-5" />}
          color="purple"
        />
      </div>

      {/* Progress History */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold">Progress Over Time</h3>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-end space-x-2">
            {data.progressHistory.map((point, index) => (
              <div
                key={index}
                className="flex-1 bg-primary-100 rounded-t"
                style={{
                  height: `${point.progress}%`,
                  transition: 'height 0.3s ease'
                }}
              >
                <div className="text-xs text-center mt-2 transform -rotate-45 origin-top-left">
                  {point.date}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Skill Breakdown */}
      <Card>
        <CardHeader>
          <h3 className="font-semibold">Skill Breakdown</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.skillBreakdown.map((skill) => (
              <div key={skill.skill}>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">{skill.skill}</span>
                  <span className="text-sm text-gray-600">{skill.progress}%</span>
                </div>
                <Progress value={skill.progress} />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: 'blue' | 'yellow' | 'green' | 'purple';
  children?: React.ReactNode;
}

function StatCard({ title, value, icon, color, children }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600'
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            {icon}
          </div>
          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
} 